import React, { Component } from "react";
import { Button, Alert, Card, Badge, Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import configData from "../../../config.json";
import ImportResultsReport from "../components/ImportResultsReport";

export default class ResultsStep extends Component {
  constructor(props) {
    super(props);
    this.state = {
      detailedResults: null,
      errorSummary: [],
      loading: false,
      showDetailsModal: false,
      selectedRowDetails: null,
      currentPage: 1,
      resultsPerPage: 50,
    };

    this.resultsColumns = [
      {
        name: "Row #",
        selector: (row) => row.row_number,
        sortable: true,
        width: "80px",
      },
      {
        name: "Status",
        selector: (row) => row.operation_status,
        sortable: true,
        width: "100px",
        cell: (row) => (
          <Badge bg={row.operation_status === "success" ? "success" : "danger"}>
            {row.operation_status}
          </Badge>
        ),
      },
      {
        name: "Operation",
        selector: (row) => row.operation_type,
        sortable: true,
        width: "100px",
        cell: (row) => (
          <Badge
            bg={
              row.operation_type === "insert"
                ? "primary"
                : row.operation_type === "update"
                ? "warning"
                : row.operation_type === "skip"
                ? "secondary"
                : "danger"
            }
          >
            {row.operation_type}
          </Badge>
        ),
      },
      {
        name: "Record ID",
        selector: (row) => row.record_id,
        sortable: true,
        width: "100px",
        cell: (row) => row.record_id || "-",
      },
      {
        name: "Error/Message",
        selector: (row) => row.error_message,
        cell: (row) => (
          <div>
            {row.error_message ? (
              <span className="text-danger small">{row.error_message}</span>
            ) : (
              <span className="text-success small">Success</span>
            )}
          </div>
        ),
      },
      {
        name: "Processing Time",
        selector: (row) => row.processing_time_ms,
        sortable: true,
        width: "120px",
        cell: (row) =>
          row.processing_time_ms ? `${row.processing_time_ms}ms` : "-",
      },
      {
        name: "Actions",
        cell: (row) => (
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => this.viewRowDetails(row)}
          >
            <i className="fas fa-eye"></i>
          </Button>
        ),
        width: "80px",
      },
    ];
  }

  componentDidMount() {
    this.fetchDetailedResults();
  }

  fetchDetailedResults = (page = 1) => {
    this.setState({ loading: true, currentPage: page });

    const url = `${configData.api_url}C_BulkImport/results?session_id=${this.props.session.sessionId}&page=${page}&limit=${this.state.resultsPerPage}`;

    fetch(url)
      .then((response) => response.json())
      .then((json) => {
        this.setState({ loading: false });

        if (json.status === "success") {
          this.setState({
            detailedResults: {
              data: json.logs || [],
              total: json.total_logs || 0,
              pagination: json.pagination || {},
            },
            errorSummary: json.error_summary || [],
          });
        } else {
          this.props.onError(
            json.message || "Failed to fetch detailed results"
          );
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ loading: false });
        this.props.onError("Network error occurred");
      });
  };

  viewRowDetails = (row) => {
    this.setState({
      showDetailsModal: true,
      selectedRowDetails: row,
    });
  };

  downloadReport = () => {
    window.open(
      `${configData.api_url}C_BulkImport/download_report?session_id=${this.props.session.sessionId}`,
      "_blank"
    );
  };

  downloadFailedRecords = () => {
    // This would be a separate endpoint to download only failed records
    window.open(
      `${configData.api_url}C_BulkImport/download_failed?session_id=${this.props.session.sessionId}`,
      "_blank"
    );
  };

  formatProcessingTime = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";

    const start = new Date(startTime);
    const end = new Date(endTime);
    const diff = end - start;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  renderSummaryCard = () => {
    const { session } = this.props;
    const successRate =
      session.totalRows > 0
        ? Math.round((session.successCount / session.totalRows) * 100)
        : 0;

    return (
      <div className="results-summary">
        <div className="summary-header">
          <h3>
            <i className="fas fa-flag-checkered me-2"></i>
            Import Complete!
          </h3>
          <p className="mb-0">Session: {session.sessionCode}</p>
        </div>

        <div className="summary-stats">
          <div className="summary-stat">
            <div className="summary-number">{session.totalRows}</div>
            <div className="summary-label">Total Records</div>
          </div>
          <div className="summary-stat">
            <div className="summary-number">{session.successCount}</div>
            <div className="summary-label">Successful</div>
          </div>
          <div className="summary-stat">
            <div className="summary-number">{session.insertCount}</div>
            <div className="summary-label">Inserted</div>
          </div>
          <div className="summary-stat">
            <div className="summary-number">{session.updateCount}</div>
            <div className="summary-label">Updated</div>
          </div>
          <div className="summary-stat">
            <div className="summary-number">{session.failedCount}</div>
            <div className="summary-label">Failed</div>
          </div>
          <div className="summary-stat">
            <div className="summary-number">{successRate}%</div>
            <div className="summary-label">Success Rate</div>
          </div>
        </div>
      </div>
    );
  };

  renderErrorSummary = () => {
    const { errorSummary } = this.state;

    if (!errorSummary || errorSummary.length === 0) {
      return null;
    }

    return (
      <div className="error-summary">
        <h6 className="mb-3">
          <i className="fas fa-exclamation-triangle text-warning me-2"></i>
          Common Error Types
        </h6>
        {errorSummary.map((error, index) => (
          <div key={index} className="error-item-summary">
            <div className="error-message">{error.error_message}</div>
            <div className="error-count">{error.error_count}</div>
          </div>
        ))}
      </div>
    );
  };

  renderDetailedResults = () => {
    const { detailedResults, loading } = this.state;

    if (loading) {
      return (
        <Card>
          <Card.Body className="text-center py-4">
            <div className="spinner-border text-primary" role="status"></div>
            <div className="mt-2">Loading detailed results...</div>
          </Card.Body>
        </Card>
      );
    }

    if (!detailedResults || detailedResults.data.length === 0) {
      return (
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          No detailed results available.
        </Alert>
      );
    }

    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-list me-2"></i>
              Detailed Results ({detailedResults.total} records)
            </h6>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  this.fetchDetailedResults(this.state.currentPage)
                }
              >
                <i className="fas fa-refresh me-1"></i>
                Refresh
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <DataTable
            columns={this.resultsColumns}
            data={detailedResults.data}
            pagination
            paginationServer
            paginationTotalRows={detailedResults.total}
            onChangeRowsPerPage={(newPerPage) => {
              this.setState({ resultsPerPage: newPerPage });
              this.fetchDetailedResults(1);
            }}
            onChangePage={(page) => this.fetchDetailedResults(page)}
            paginationPerPage={this.state.resultsPerPage}
            paginationRowsPerPageOptions={[25, 50, 100]}
            highlightOnHover
            striped
            responsive
            customStyles={{
              headCells: {
                style: {
                  backgroundColor: "#f8f9fa",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                },
              },
              rows: {
                style: {
                  fontSize: "0.85rem",
                },
              },
            }}
          />
        </Card.Body>
      </Card>
    );
  };

  renderRowDetailsModal = () => {
    const { showDetailsModal, selectedRowDetails } = this.state;

    if (!selectedRowDetails) return null;

    const excelData = selectedRowDetails.excel_row_data
      ? JSON.parse(selectedRowDetails.excel_row_data)
      : {};
    const mappedData = selectedRowDetails.mapped_data
      ? JSON.parse(selectedRowDetails.mapped_data)
      : {};
    const validationErrors = selectedRowDetails.validation_errors
      ? JSON.parse(selectedRowDetails.validation_errors)
      : {};

    return (
      <Modal
        show={showDetailsModal}
        onHide={() => this.setState({ showDetailsModal: false })}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Row #{selectedRowDetails.row_number} Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <h6>Original Excel Data</h6>
              <div
                className="border rounded p-2 mb-3"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                {Object.entries(excelData).map(([key, value]) => (
                  <div key={key} className="small mb-1">
                    <strong>{key}:</strong> {value || "-"}
                  </div>
                ))}
              </div>
            </div>
            <div className="col-md-6">
              <h6>Mapped Database Data</h6>
              <div
                className="border rounded p-2 mb-3"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                {Object.entries(mappedData).map(([key, value]) => (
                  <div key={key} className="small mb-1">
                    <strong>{key}:</strong> {value || "-"}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <h6>Processing Information</h6>
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <td>
                      <strong>Status:</strong>
                    </td>
                    <td>
                      <Badge
                        bg={
                          selectedRowDetails.operation_status === "success"
                            ? "success"
                            : "danger"
                        }
                      >
                        {selectedRowDetails.operation_status}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Operation:</strong>
                    </td>
                    <td>
                      <Badge
                        bg={
                          selectedRowDetails.operation_type === "insert"
                            ? "primary"
                            : selectedRowDetails.operation_type === "update"
                            ? "warning"
                            : selectedRowDetails.operation_type === "skip"
                            ? "secondary"
                            : "danger"
                        }
                      >
                        {selectedRowDetails.operation_type}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Record ID:</strong>
                    </td>
                    <td>{selectedRowDetails.record_id || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Processing Time:</strong>
                    </td>
                    <td>{selectedRowDetails.processing_time_ms}ms</td>
                  </tr>
                  {selectedRowDetails.error_message && (
                    <tr>
                      <td>
                        <strong>Error:</strong>
                      </td>
                      <td className="text-danger">
                        {selectedRowDetails.error_message}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {Object.keys(validationErrors).length > 0 && (
            <div className="row">
              <div className="col-12">
                <h6>Validation Errors</h6>
                <div className="border rounded p-2 bg-light">
                  {Object.entries(validationErrors).map(([field, errors]) => (
                    <div key={field} className="mb-2">
                      <strong className="text-danger">{field}:</strong>
                      <ul className="mb-0 ms-3">
                        {errors.map((error, idx) => (
                          <li key={idx} className="small">
                            {error.message || error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => this.setState({ showDetailsModal: false })}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  calculateProcessingTime = () => {
    const { session } = this.props;
    if (!session.startTime || !session.endTime) return "N/A";
    
    const startTime = new Date(session.startTime);
    const endTime = new Date(session.endTime);
    const diffMs = endTime - startTime;
    
    if (diffMs < 1000) return `${diffMs}ms`;
    if (diffMs < 60000) return `${Math.round(diffMs / 1000)}s`;
    if (diffMs < 3600000) return `${Math.round(diffMs / 60000)}min`;
    return `${Math.round(diffMs / 3600000)}h`;
  };

  retryFailedRecords = (sessionId) => {
    console.log("Retrying failed records for session:", sessionId);
    // Implementation for retrying failed records
    // This would typically involve creating a new import session with only failed records
  };

  render() {
    const { session } = this.props;

    // Create results object for the new comprehensive reporting component
    const importResults = {
      insert_count: session.insertCount || 0,
      update_count: session.updateCount || 0,
      skip_count: session.skipCount || 0,
      failed_count: session.failedCount || 0,
      processing_time: this.calculateProcessingTime(),
      created_details: this.state.detailedResults?.data?.filter(r => r.operation_status === 'inserted') || [],
      updated_details: this.state.detailedResults?.data?.filter(r => r.operation_status === 'updated') || [],
      skipped_details: this.state.detailedResults?.data?.filter(r => r.operation_status === 'skipped') || [],
      failed_details: this.state.detailedResults?.data?.filter(r => r.operation_status === 'failed') || [],
    };

    return (
      <div>
        <ImportResultsReport
          results={importResults}
          session={session}
          onDownloadReport={this.downloadReport}
          onRetryFailed={this.retryFailedRecords}
          onClose={() => window.location.reload()}
        />
        
        {/* Legacy detailed results - show as additional tab if needed */}
        <div className="mt-4">
          <div className="row">
            <div className="col-12">{this.renderDetailedResults()}</div>
          </div>
        </div>

        {session.failedCount > 0 && (
          <Alert variant="warning" className="mt-4">
            <h6 className="alert-heading">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Some Records Failed to Import
            </h6>
            <p className="mb-2">
              {session.failedCount} records failed to import due to validation
              errors or data issues.
            </p>
            <ul className="mb-0">
              <li>
                Download the failed records report to see detailed error
                information
              </li>
              <li>Fix the data issues in your Excel file</li>
              <li>Re-upload the corrected file for a new import session</li>
              <li>
                The system will automatically skip duplicates if configured
              </li>
            </ul>
          </Alert>
        )}

        {/* Additional Information */}
        <div className="row mt-4">
          <div className="col-12">
            <Alert variant="info">
              <h6 className="alert-heading">
                <i className="fas fa-lightbulb me-2"></i>
                What's Next?
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <ul className="mb-0">
                    <li>Review the detailed results above</li>
                    <li>Download the complete report for your records</li>
                    <li>Check your database to verify imported data</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="mb-0">
                    <li>Fix any failed records and re-import if needed</li>
                    <li>Save successful mappings as templates</li>
                    <li>Consider setting up recurring imports</li>
                  </ul>
                </div>
              </div>
            </Alert>
          </div>
        </div>

        {this.renderRowDetailsModal()}
      </div>
    );
  }
}
